import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Skeleton,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { organizationService, Organization } from '@services/organizationService';
import { toDisplayError } from '../utils/apiError';

// Validation schema for creating organization
const createOrgSchema = z.object({
  name: z
    .string()
    .min(1, 'organizations.validation.nameRequired')
    .max(255, 'organizations.validation.nameTooLong'),
  slug: z
    .string()
    .min(1, 'organizations.validation.slugRequired')
    .max(100, 'organizations.validation.slugTooLong')
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'organizations.validation.slugFormat'),
  description: z.string().optional(),
});

type CreateOrgFormData = z.infer<typeof createOrgSchema>;

function OrganizationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // REV-687: the dashboard's "Create an organization" button used to land here
  // and stop, leaving the user to find and press Create again — a button that
  // names an action but only takes you near it. `?create=1` lets the caller
  // express the whole intent, so the dialog is open on arrival.
  //
  // A query parameter rather than router state on purpose: it survives a
  // reload and can be linked to or shared, which router state cannot.
  const [searchParams, setSearchParams] = useSearchParams();
  const [createDialogOpen, setCreateDialogOpen] = useState(searchParams.get('create') === '1');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CreateOrgFormData>({
    resolver: zodResolver(createOrgSchema),
    defaultValues: {
      name: '',
      slug: '',
      description: '',
    },
  });

  const watchName = watch('name');
  const watchSlug = watch('slug');

  // Auto-generate slug from name (only if user hasn't manually edited the slug)
  useEffect(() => {
    if (watchName && !slugManuallyEdited) {
      const generatedSlug = watchName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setValue('slug', generatedSlug);
    }
  }, [watchName, slugManuallyEdited, setValue]);

  // Check slug availability with debounce
  useEffect(() => {
    if (!watchSlug || watchSlug.length < 1) {
      setSlugAvailable(null);
      return;
    }

    const timer = setTimeout(async () => {
      setCheckingSlug(true);
      try {
        const result = await organizationService.checkSlug(watchSlug);
        setSlugAvailable(result.available);
      } catch {
        setSlugAvailable(null);
      } finally {
        setCheckingSlug(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [watchSlug]);

  // Fetch organizations
  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await organizationService.list();
      setOrganizations(result.items ?? []);
    } catch (err) {
      setError(t('organizations.errors.fetchFailed'));
      console.error('Failed to fetch organizations:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchOrganizations();
  }, [fetchOrganizations]);

  const handleCreateOrg = async (data: CreateOrgFormData) => {
    setCreating(true);
    setCreateError(null);

    // Check slug availability before submitting
    if (slugAvailable === false) {
      setCreateError(t('organizations.validation.slugTaken'));
      setCreating(false);
      return;
    }

    // If slug hasn't been checked yet, check it now
    if (slugAvailable === null && data.slug) {
      try {
        const result = await organizationService.checkSlug(data.slug);
        if (!result.available) {
          setSlugAvailable(false);
          setCreateError(t('organizations.validation.slugTaken'));
          setCreating(false);
          return;
        }
        setSlugAvailable(true);
      } catch {
        // Continue with submission, server will validate
      }
    }

    try {
      const newOrg = await organizationService.create({
        name: data.name,
        slug: data.slug,
        description: data.description || undefined,
      });
      setCreateDialogOpen(false);
      reset();
      // Navigate to the new organization
      void navigate(`/orgs/${newOrg.slug}`);
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } } };
      setCreateError(toDisplayError(error, t('organizations.errors.createFailed')));
    } finally {
      setCreating(false);
    }
  };

  const handleOpenCreateDialog = () => {
    reset();
    setSlugAvailable(null);
    setCreateError(null);
    setSlugManuallyEdited(false);
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    // Drop ?create=1 so a reload, or Back, does not reopen the dialog the user
    // has just dismissed.
    if (searchParams.get('create')) {
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
    setCreateDialogOpen(false);
    reset();
  };

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('organizations.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {t('organizations.subtitle')}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
          {t('organizations.createNew')}
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Organizations Grid */}
      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="circular" width={48} height={48} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="text" width="80%" sx={{ mt: 2 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : organizations.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('organizations.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('organizations.empty.description')}
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
              {t('organizations.createFirst')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {organizations.map((org) => (
            <Grid item xs={12} sm={6} md={4} key={org.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                      }}
                    >
                      {org.logo_url ? (
                        <Box
                          component="img"
                          src={org.logo_url}
                          alt=""
                          sx={{ width: 40, height: 40, borderRadius: 0.5 }}
                        />
                      ) : (
                        <BusinessIcon sx={{ color: 'white', fontSize: 28 }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" noWrap>
                        {org.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        /{org.slug}
                      </Typography>
                    </Box>
                  </Box>
                  {org.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {org.description}
                    </Typography>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      label={t(`organizations.statuses.${org.status}`)}
                      size="small"
                      color={org.status === 'active' ? 'success' : 'default'}
                    />
                  </Box>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  {/* REV-732: goes to a URL, so it is a link. */}
                  <Button
                    size="small"
                    startIcon={<SettingsIcon />}
                    component={RouterLink}
                    to={`/orgs/${org.slug}`}
                  >
                    {t('organizations.manage')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Organization Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(handleCreateOrg)}>
          <DialogTitle>{t('organizations.create.title')}</DialogTitle>
          <DialogContent>
            {createError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createError}
              </Alert>
            )}
            <TextField
              {...register('name')}
              label={t('organizations.fields.name')}
              fullWidth
              margin="normal"
              error={!!errors.name}
              helperText={errors.name ? t(errors.name.message as string) : undefined}
              disabled={creating}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              {...register('slug', {
                onChange: () => setSlugManuallyEdited(true),
              })}
              label={t('organizations.fields.slug')}
              fullWidth
              margin="normal"
              error={!!errors.slug || slugAvailable === false}
              helperText={
                errors.slug
                  ? t(errors.slug.message as string)
                  : slugAvailable === false
                    ? t('organizations.validation.slugTaken')
                    : slugAvailable === true
                      ? t('organizations.validation.slugAvailable')
                      : t('organizations.fields.slugHint')
              }
              disabled={creating}
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  endAdornment: checkingSlug ? <CircularProgress size={20} /> : undefined,
                },
              }}
            />
            <TextField
              {...register('description')}
              label={t('organizations.fields.description')}
              fullWidth
              margin="normal"
              multiline
              rows={3}
              error={!!errors.description}
              helperText={errors.description ? t(errors.description.message as string) : undefined}
              disabled={creating}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            {/* REV-688: no Website field here. It is stored, validated by the
                API on both create and update, and editable on the
                organization's General settings tab — but asking for it at the
                moment of creation lengthens the one dialog that should be as
                short as possible, for a value nothing in the product consumes
                yet. Removed from this dialog only; the column, the API and the
                settings field are untouched. */}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseCreateDialog} disabled={creating}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="contained" disabled={creating}>
              {creating ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('organizations.create.submit')
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default OrganizationsPage;
